import { useMemo } from 'react';
import { PlusIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { TextInput } from '@interstate/components/TextInput';
import { NumericInput } from '@interstate/components/NumericInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useCustomerCardContext } from '../CustomerCardProvider';
import { CustomerFieldsAccordionContainer } from './HousingAndEmploymentInfo.style';
import { NameFieldsWrapper } from '../CustomerCard.styles';
import { OTHER_INCOME_FIELD, OTHER_INCOME_SOURCE_FIELD } from '../constants';
import { CustomerFieldsLayoutHeader } from './CustomerFieldsLayoutHeader';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';
import { Box } from '@interstate/components/Box';

export const AdditionalIncome = () => {
    const {
        isAdditionalFieldsExpanded,
        setIsAdditionalFieldsExpanded,
        isLocked,
        formValues,
        handleInputChange,
        handleNumberInputChange,
        customerFormErrors
    } = useCustomerCardContext();

    const financialInfoFormErrors: { otherIncomeSource: ErrorState; otherIncome: ErrorState } = useMemo(
        () => ({
            otherIncomeSource: (customerFormErrors?.financialInfo as Record<string, ErrorState>)?.otherIncomeSource ?? {
                hasError: false,
                errorMessage: ''
            },
            otherIncome: (customerFormErrors?.financialInfo as Record<string, ErrorState>)?.otherIncome ?? {
                hasError: false,
                errorMessage: ''
            }
        }),
        [customerFormErrors]
    );

    const handleToggle = () => {
        setIsAdditionalFieldsExpanded((prev) => !prev);
    };

    const handleDelete = () => {
        handleInputChange(OTHER_INCOME_SOURCE_FIELD, '');
        handleNumberInputChange(OTHER_INCOME_FIELD, '');
        setIsAdditionalFieldsExpanded(false);
    };

    const renderIncomeSourceField = () => {
        return (
            <TextInput
                name="additionalIncomeSource"
                displayLabel={true}
                label="Additional Source of Income"
                value={formValues.financialInfo?.otherIncomeSource}
                onChange={(e) => handleInputChange(OTHER_INCOME_SOURCE_FIELD, e.target.value)}
                disabled={isLocked}
                hasError={Boolean(financialInfoFormErrors.otherIncomeSource.hasError)}
                errorMessage={financialInfoFormErrors.otherIncomeSource.errorMessage || ''}
                data-testid="customer-form-additional-income-source"
                size="small"
            />
        );
    };

    const renderIncomeMonthlyField = () => {
        return (
            <NumericInput
                name="additionalMonthlyIncome"
                displayLabel={true}
                label="Additional Monthly Income"
                value={String(formValues.financialInfo?.otherIncome ?? '')}
                allowDecimal={true}
                autoInsertCommas={true}
                decimalMaxLength={2}
                decimalMinLength={0}
                inputPrefix="$"
                onChange={(e: InterstateOnChangeEvent<string>) => handleNumberInputChange(OTHER_INCOME_FIELD, e.target.value)}
                disabled={isLocked}
                hasError={Boolean(financialInfoFormErrors.otherIncome.hasError)}
                errorMessage={financialInfoFormErrors.otherIncome.errorMessage || ''}
                data-testid="customer-form-additional-monthly-income"
                size="small"
            />
        );
    };

    return (
        <CustomerFieldsAccordionContainer className="additional-income-section" data-testid="additional-income-section">
            {!isAdditionalFieldsExpanded ? (
                <Action
                    data-testid="additional-income-button"
                    actionIcon={{ icon: <PlusIcon />, start: true }}
                    onClick={handleToggle}
                >
                    Add Additional Income
                </Action>
            ) : (
                <Box paddingTop=".5rem">
                    <CustomerFieldsLayoutHeader
                        onDelete={handleDelete}
                        deleteTestId="additional-income-delete-button"
                        disabled={isLocked}
                    >
                        Additional Income
                    </CustomerFieldsLayoutHeader>
                    <NameFieldsWrapper columns={4}>
                        {renderIncomeSourceField()}
                        {renderIncomeMonthlyField()}
                    </NameFieldsWrapper>
                </Box>
            )}
        </CustomerFieldsAccordionContainer>
    );
};
